import type { MailProviderId } from '@agentic-flow-studio/shared/lib/mail-provider-availability';
import { Mail, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Skeleton } from '../../ui/skeleton';
import { MailProviderConnectActions } from './MailProviderConnectActions';

interface ConnectedAccountsSectionProps {
  /** Selects the correct branded origin for the post-OAuth workspace settings return. */
  portal: 'clinic' | 'skill-viper';
  isReadOnly: boolean;
  getAuthToken: () => Promise<string | null>;
}

/**
 * Which providers appear here, in what order, and whether each may be connected are all
 * server-resolved (`gmail.getMailProviders`). This component deliberately holds no provider
 * list of its own: availability tracks external vendor approvals, and a client that decided
 * for itself would be wrong for as long as it took to ship a release — indefinitely, for a
 * native build already in the field.
 */
export function ConnectedAccountsSection({
  portal,
  isReadOnly,
  getAuthToken,
}: ConnectedAccountsSectionProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const utils = trpc.useUtils();
  const { data: accounts, isLoading } = trpc.gmail.getConnectedExternalAccounts.useQuery();
  const { data: mailProviders } = trpc.gmail.getMailProviders.useQuery();
  const { data: joinedWaitlists } = trpc.gmail.getMailProviderWaitlist.useQuery();

  const joinWaitlistMutation = trpc.gmail.joinMailProviderWaitlist.useMutation({
    onSuccess: () => {
      toast.success('You are on the waitlist — we will email you when it opens.');
      utils.gmail.getMailProviderWaitlist.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Could not join the waitlist');
    },
  });

  const disconnectMutation = trpc.gmail.disconnectAccount.useMutation({
    onSuccess: () => {
      toast.success('Account disconnected');
      utils.gmail.getConnectedExternalAccounts.invalidate();
    },
  });

  /**
   * Start an OAuth flow.
   *
   * The `type` query is Google-only: Gmail has a read-only scope that still permits
   * labelling, so the split is meaningful there. Microsoft Graph has no such middle
   * ground — the scopes that let us categorise and archive are the same ones that let
   * us read — so Outlook has a single button rather than a read-only option that would
   * be a lie.
   */
  const startOAuth = async (path: string, query: Record<string, string> = {}) => {
    setIsConnecting(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const workspaceId = localStorage.getItem('workspaceId');
      const organizationId = localStorage.getItem('organizationId');

      const params = new URLSearchParams({
        workspaceId: workspaceId ?? '',
        organizationId: organizationId ?? '',
        portal,
        ...query,
      });
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}${path}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { url } = (await response.json()) as { url: string };
      window.location.href = url;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error initiating OAuth: ${message}`);
      setIsConnecting(false);
    }
  };

  const description =
    'Connect your email accounts to enable automatic summarization and draft generation.';

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <CardHeader className="border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <CardTitle>Connected Accounts</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
            </div>
          ) : accounts && accounts.length > 0 ? (
            <div className="grid gap-4">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-background/50 group transition-all hover:border-primary/30"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{account.email}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant="secondary"
                          className="text-[10px] [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)] font-bold py-0 h-4"
                        >
                          {account.provider}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Added {new Date(account.createdAt * 1000).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {!isReadOnly && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => disconnectMutation.mutate({ accountId: account.id })}
                        disabled={disconnectMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-border/50 rounded-2xl bg-muted/10">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium">No accounts connected</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-[280px] mx-auto">
                Connect your first mailbox to start summarizing your inbox.
              </p>
            </div>
          )}

          {!isReadOnly && (
            <div className="space-y-4 pt-4 border-t border-border/50">
              {(mailProviders ?? []).map((descriptor, index) => (
                <MailProviderConnectActions
                  key={descriptor.provider}
                  descriptor={descriptor}
                  isPrimary={index === 0}
                  isConnecting={isConnecting}
                  onConnect={startOAuth}
                  hasJoinedWaitlist={(joinedWaitlists ?? []).includes(descriptor.provider)}
                  isJoiningWaitlist={joinWaitlistMutation.isPending}
                  onJoinWaitlist={(provider: MailProviderId) =>
                    joinWaitlistMutation.mutate({ provider })
                  }
                />
              ))}

              <p className="text-[10px] text-center text-muted-foreground">
                Full Access includes permission to create drafts on your behalf. We will never send
                emails without your confirmation. Outlook has no read-only equivalent — Microsoft
                grants reading and organising together.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
